import {Controller} from "@hotwired/stimulus"
import Swal from "sweetalert2";

export default class extends Controller {
    static targets = ["search", "dockSearch", "form", "continueBtn", "promotions", "mobileContinueBtn"]
    static values = {
        draftId: Number,
        draftSgid: String,
        invitationSlug: String,
        fields: Object,
        disableDraftHydrate: {type: Boolean, default: false},
        context: {type: String, default: "draft"},     // "draft" | "party"
        partyToken: String
    };

    // bound handlers (set in connect)
    _onFrameLoadBound = null;
    _pickCharityBound = null;

    // track “Show results” mode on mobile
    _mobilePendingResults = false;

    connect() {
        this.timer = null

        // bind once so we can remove later
        this._onFrameLoadBound = this._onFrameLoad.bind(this);
        this._pickCharityBound = this.pickCharity.bind(this);
        document.addEventListener('turbo:frame-load', this._onFrameLoadBound);

        this._togglePromotions()
        if (!this.disableDraftHydrateValue) {
            const resultsWrapper = document.querySelector('.charity-list-wrapper')
            if (resultsWrapper) {
                resultsWrapper.addEventListener('click', this._pickCharityBound)
            }

            // Prime cache then apply selection/ui once
            this._primeFieldsCache().then(() => this._rehydrateFromDraft(true))
            this._setMobileResultsPending(false); // do not "show results" until search or category toggle
        }
    }

    disconnect() {
        if (this.timer) clearTimeout(this.timer);
        if (this._onFrameLoadBound) document.removeEventListener('turbo:frame-load', this._onFrameLoadBound);
        this._onFrameLoadBound = null;
        this._pickCharityBound = null;
    }

    // Submit the nearest form (plain HTML request — no Turbo targeting)
    submit(event) {
        this._togglePromotions()
        if (this.timer) clearTimeout(this.timer)
        this.timer = setTimeout(() => {
            const source = event?.target || null
            const form = (source && source.closest && source.closest('form'))
                || this.formTargets?.[0]
                || document.getElementById('charity-directory-filters-form-mobile')
                || document.getElementById('charity-directory-filters-form')
            if (!form) return
            if (typeof form.requestSubmit === 'function') form.requestSubmit();
            else form.submit();

            // On mobile, switch the bottom button into “Show results” mode
            if (this._isMobile()) {
                this._setMobileResultsPending(true);
            }
        }, 180)
    }

    _onFrameLoad(e) {
        // When the results frame swaps (pagination/search), re-apply selection & listeners
        const frame = e.target;
        if (!frame || frame.id !== 'charity_results') return;

        // re-bind click delegation on the new wrapper
        const wrap = frame.querySelector('.charity-list-wrapper') || document.querySelector('.charity-list-wrapper');
        if (wrap && this._pickCharityBound) wrap.addEventListener('click', this._pickCharityBound);

        // re-apply selection highlight and button state
        const cid = this._cachedFields?.party?.charity_id;
        if (cid) {
            this._applySelectionUI(String(cid));
            this._enableContinue(true);
        } else {
            //If we’re on mobile and waiting for “Show results”, don’t override that state
            if (!(this._isMobile() && this._mobilePendingResults)) {
                this._enableContinue(false);
            }
        }
    }

    _togglePromotions() {
        const q = this.hasSearchTarget ? (this.searchTarget.value || "").trim() : ""
        const shouldHide = q.length > 0
        this.promotionsTargets.forEach(el => el.classList.toggle("d-none", shouldHide))
    }

    // Bottom dock search (mobile). Mirrors value into the active filters form then submits.
    searchFromDock(event) {
        const v = event.target.value || ""

        // Prefer mobile form when present/visible; fall back to desktop form
        let form = document.getElementById('charity-directory-filters-form-mobile')
        const isMobile = window.matchMedia('(max-width: 991.98px)').matches
        if (!isMobile || !form) form = document.getElementById('charity-directory-filters-form')

        if (!form) return
        const q = form.querySelector('input[name="q"]')
        if (q) q.value = v

        // Reuse the same submit path
        this.submit({target: form})
    }

    pickCharity(event) {
        const item = event.target.closest('[data-charity-id]')
        if (!item) return
        const charityId = item.getAttribute('data-charity-id')
        if (!charityId) return

        // Remove is-selected from all items with data-charity-id
        const allItems = document.querySelectorAll('[data-charity-id]')
        allItems.forEach(el => el.classList.remove('is-selected'))

        // Add is-selected to clicked item
        item.classList.add('is-selected')

        // Persist only the minimal delta to avoid data loss
        this._mergeAndPatch({party: {charity_id: String(charityId)}})

        // Immediately update local cache so button state logic sees the new selection
        if (!this._cachedFields) this._cachedFields = {}
        if (!this._cachedFields.party) this._cachedFields.party = {}
        this._cachedFields.party.charity_id = String(charityId)

        // Enable the button visually and functionally
        this._enableContinue(true)
        this._applySelectionUI(String(charityId))

        // Once a charity is chosen, button should act as normal Continue again
        this._setMobileResultsPending(false)
    }

    _applySelectionUI(charityId) {
        if (!charityId) return

        // Remove is-selected from all items
        const allItems = document.querySelectorAll('[data-charity-id]')
        allItems.forEach(el => el.classList.remove('is-selected'))

        // Find the element with the matching charityId
        const selected = document.querySelector(`[data-charity-id="${charityId}"]`)
        if (selected) {
            selected.classList.add('is-selected')
        }
    }

    async _rehydrateFromDraft(force = false) {
        if (force || !this._cachedFields) {
            await this._primeFieldsCache();
        }
        const cid = this._cachedFields?.party?.charity_id;
        if (cid) {
            this._applySelectionUI(String(cid));
            this._enableContinue(true);
        } else {
            this._enableContinue(false);
        }
    }

    _headers() {
        return {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
    }

    _enableContinue(enabled) {
        this.continueBtnTargets.forEach(btn => {
            if (enabled) {
                btn.classList.remove('grey-button')
                btn.classList.add('blue-button')
                btn.disabled = false
            } else {
                btn.classList.add('grey-button')
                btn.classList.remove('blue-button')
                btn.disabled = true
            }
        })
    }

    _setMobileResultsPending(pending) {
        this._mobilePendingResults = pending;
        if (!this.hasMobileContinueBtnTarget) return;

        const btn = this.mobileContinueBtnTarget;
        const baseLabel = (this.contextValue === "party") ? "Save changes" : "Continue";

        if (pending) {
            // Mobile "Show results" mode: always enabled + blue
            btn.textContent = "Show results";
            btn.disabled = false;
            btn.classList.remove('grey-button');
            btn.classList.add('blue-button');
        } else {
            // Back to normal label/state driven by selected charity.
            btn.textContent = baseLabel;
            const cid = this._cachedFields?.party?.charity_id;
            this._enableContinue(!!cid);
        }
    }

    _safeJSON(s) {
        if (!s) return null;
        try {
            return typeof s === 'string' ? JSON.parse(s) : s;
        } catch (_) {
            return null;
        }
    }

    async _fetchServerFields() {
        try {
            // Edit mode: fetch live party fields
            if (this.contextValue === "party" && this.hasPartyTokenValue) {
                const res = await fetch(`/party/${encodeURIComponent(this.partyTokenValue)}/fields`);
                if (!res.ok) return {};
                const json = await res.json();
                return json?.fields_json || {};
            }
            // Setup mode: fetch draft fields
            if (this.hasDraftIdValue && this.draftIdValue) {
                const res = await fetch(`/party_drafts/${this.draftIdValue}`);
                if (!res.ok) return {};
                const json = await res.json();
                return json?.fields_json || {};
            }
            return {};
        } catch (_) {
            return {};
        }
    }

    async _primeFieldsCache() {
        if (this._cachedFields) return;

        // 1) Prefer what the server already injected into the DOM
        const fromDom = this._safeJSON(this.fieldsValue);
        if (fromDom) {
            this._cachedFields = fromDom;
            return;
        }

        // 2) (optional) Only fall back to fetching in *draft* flow,
        // not in edit/party flow, to match how other edit screens work.
        if (this.contextValue === "draft" && this.hasDraftIdValue) {
            try {
                const res = await fetch(`/party_drafts/${encodeURIComponent(this.draftIdValue)}`);
                if (res.ok) {
                    const json = await res.json(); // expect { fields_json: ... } or similar
                    this._cachedFields = json.fields_json || {};
                }
            } catch (_) { /* ignore */
            }
        }
    }

    _mergeDeep(target, source) {
        const out = Array.isArray(target) ? target.slice() : {...target}
        if (source && typeof source === 'object') {
            for (const [k, v] of Object.entries(source)) {
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    out[k] = this._mergeDeep(out[k] || {}, v)
                } else {
                    out[k] = v
                }
            }
        }
        return out
    }

    async _saveFieldsNow(payload) {
        // Persist to Party (edit mode) or Draft (setup mode)
        try {
            if (this.contextValue === "party" && this.hasPartyTokenValue) {
                return await fetch(`/party/${encodeURIComponent(this.partyTokenValue)}/fields`, {
                    method: "PATCH",
                    headers: this._headers(),
                    body: JSON.stringify({fields_json: payload})
                });
            }
            if (this.hasDraftIdValue && this.draftIdValue) {
                return await fetch(`/party_drafts/${this.draftIdValue}`, {
                    method: "PATCH",
                    headers: this._headers(),
                    body: JSON.stringify({fields_json: payload})
                });
            }
            // If neither is available, no-op
            return {ok: false};
        } catch (_e) {
            return {ok: false};
        }
    }

    async _mergeAndPatch(patch) {
        // Re-fetch just before patching to avoid overwriting concurrent changes
        const serverFields = await this._fetchServerFields();
        const base = this._cachedFields ? this._mergeDeep(serverFields, this._cachedFields) : serverFields;
        const merged = this._mergeDeep(base, patch);

        const res = await this._saveFieldsNow(merged);
        if (res && res.ok) {
            this._cachedFields = merged;
            // Sync this controller's data-* attribute (not the invitation-form one)
            const mainContainer = this.element.closest('[data-charity-directory-filters-fields-value]');
            if (mainContainer) {
                mainContainer.setAttribute('data-charity-directory-filters-fields-value', JSON.stringify(merged));
            }
        }
    }

    _isMobile() {
        return window.matchMedia('(max-width: 991.98px)').matches;
    }

    back() {
        const qs = new URLSearchParams();
        if (this.hasDraftSgidValue && this.draftSgidValue) {
            qs.set('draft_sgid', this.draftSgidValue);
        } else if (this.hasDraftIdValue && this.draftIdValue) {
            qs.set('draft_id', String(this.draftIdValue));
        }
        if (this.hasInvitationSlugValue && this.invitationSlugValue) {
            qs.set('invitation', this.invitationSlugValue);
        }
        window.location.assign(`/party/details?${qs.toString()}`);
    }

    _toast(title, icon = 'info', opts = {}) {
        const {persist = false, delayClose = 0} = opts;
        Swal.fire({
            toast: true,
            icon,
            title,
            position: 'bottom-end',
            showConfirmButton: false,
            timer: persist ? undefined : 2200,
            timerProgressBar: !persist,
        });
        if (persist && delayClose) {
            setTimeout(() => Swal.close(), delayClose);
        }
    }

    async continue() {

        // On mobile, if filters/search were changed, this acts as “Show results”
        if (this._isMobile() && this._mobilePendingResults) {
            const offcanvas = document.getElementById('sidebar-charity');
            if (offcanvas && offcanvas.classList.contains('show')) {
                const closeBtn = offcanvas.querySelector('[data-bs-dismiss="offcanvas"]');
                if (closeBtn) {
                    closeBtn.click();      // let Bootstrap close it properly
                } else {
                    offcanvas.classList.remove('show');
                }
            }
            this._setMobileResultsPending(false);
            return; // don’t advance to next step yet
        }

        // Ensure we have the latest local cache
        if (!this._cachedFields) {
            await this._primeFieldsCache();
        }
        const cid = this._cachedFields?.party?.charity_id;
        if (!cid) return; // nothing selected yet

        // --- EDIT FLOW (live party) --------------------------------------------
        if (this.contextValue === "party" && this.hasPartyTokenValue) {
            this._toast('Saving changes…', 'info', {persist: true});
            try {
                // Persist full cached fields (charity id already merged by pickCharity)
                const res = await this._saveFieldsNow(this._cachedFields || {});
                if (!res?.ok) throw new Error(await res.text());
                this._toast('Saved!', 'success', {delayClose: 700});
                // Return to Manage Invitation
                window.location.assign(`/dashboard/manage/${encodeURIComponent(this.partyTokenValue)}`);
            } catch (e) {
                console.warn("charity save (party) failed:", e);
                this._toast('Something went wrong, please try again', 'error');
            }
            return;
        }

        // --- SETUP FLOW (draft) ------------------------------------------------
        // Advance draft status only (fields were already persisted on selection)
        if (this.hasDraftIdValue && this.draftIdValue) {
            this._toast('Setting charity...', 'info', {persist: true});
            try {
                const res = await fetch(`/party_drafts/${this.draftIdValue}`, {
                    method: 'PATCH',
                    headers: this._headers(),
                    body: JSON.stringify({status: 'additional'})
                });
                // Even if response is non-200, proceed to next step; UI shows quick success
                this._toast('All set!', 'success', {delayClose: 600});
            } catch (_) {
                // Swallow to avoid blocking navigation; selection was already saved via _mergeAndPatch
            }
        }

        // Navigate to Additional Info with the appropriate draft identifiers
        const qs = new URLSearchParams();
        if (this.hasDraftSgidValue && this.draftSgidValue) {
            qs.set('draft_sgid', this.draftSgidValue);
        } else if (this.hasDraftIdValue && this.draftIdValue) {
            qs.set('draft_id', String(this.draftIdValue));
        }
        if (this.hasInvitationSlugValue && this.invitationSlugValue) {
            qs.set('invitation', this.invitationSlugValue);
        }
        window.location.assign(`/party/additional-info?${qs.toString()}`);
    }
}